import { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { getLastSeenProductIdsStorage } from '@jsmdg/browser-storage';
import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    GA4EventName,
    trackCategoryTree,
    trackEecToggleWishlistItem,
    trackToggleWishlist,
    trackWishlistError,
    WishlistError,
    WishListEventType,
} from '@jsmdg/tracking';
import { useWishlist, WishlistErrorTypes } from '@jsmdg/wishlist-library';
import { type WishlistResponseError } from '@jsmdg/wishlist-library/dist/esm/client/types';
import {
    Breakpoint,
    DiscountBadge,
    DiscountBadgeShape,
    NotificationSnackbar,
    SnackbarPosition,
    useBreakpoint,
    useSnackbarContext,
    WishlistButton,
    WishlistButtonType,
} from '@jsmdg/yoshi';
import { NotificationVariant } from '@jsmdg/yoshi/dist/enums/notification';
import { ProductPromotionType, ProductPromotionTypeTrackingLabel } from '../../../shared/enums';
import { isExperience, isValueVoucher } from '../../../shared/product-safeguards';
import { getPurchaseVariantForTracking } from '../../helper/getPurchaseType';
import { useBuybox } from '../../hooks/useBuybox';
import { useProduct } from '../../hooks/useProduct';
import { type ProductDetailContext } from '../../types/types';
import { AttributesContainer } from '../AttributesContainer';
import { BuyboxContainer } from '../BuyboxContainer';
import { ImageGalleryWithFallback } from '../ImageGallery/ImageGalleryWithFallback';
import { ProductInfo } from '../ProductInfo';
import styles from './ProductDetail.module.scss';

const messages = defineMessages({
    screenReaderTextForWishlistRemove: {
        defaultMessage: 'Von der Wunschliste entfernen',
    },
    screenReaderTextForWishlistAdd: {
        defaultMessage: 'Zur Wunschliste hinzufügen',
    },
    tooManyWishlistArticlesError: {
        defaultMessage:
            'Die maximale Anzahl an Erlebnissen wurde erreicht – bitte entferne Artikel, um Platz für neue zu schaffen.',
    },
    a11YCloseErrorMessage: { defaultMessage: 'Schließen' },
});

const ProductDetail = (): JSX.Element => {
    const boxClusterAreaRef = useRef<HTMLHeadingElement>(null);

    const { purchaseType, seatCount } = useBuybox();
    const product = useProduct();

    const {
        attributes,
        discountBadge,
        images,
        manufacturerSku,
        originalPrice,
        price,
        productId,
        title,
        trackingName,
        trackingPath,
        type,
        url,
    } = product;

    const {
        customerState,
        featureFlagsVariation,
        initialWishlist,
        isBoxProductWithQrCodeLink,
        isMydays,
    } = useFragmentContext<ProductDetailContext>();
    const { isInWishlist, toggleWishlistItem } = useWishlist(customerState, initialWishlist);
    const upToXSBreakpoint = useBreakpoint(Breakpoint.XS);
    const intl = useIntl();

    const { openSnackbar } = useSnackbarContext();
    const isProductInWishlist = isInWishlist(productId);

    const isRecommendationsEnabled =
        featureFlagsVariation?.google_trafic_recommendations === 'pages' ||
        featureFlagsVariation?.google_trafic_recommendations === 'products';

    useEffect(() => {
        getLastSeenProductIdsStorage().add(productId);
    }, [productId]);

    useEffect(() => {
        trackCategoryTree(trackingPath);
    }, [trackingPath]);

    const toggleWishlistItemOnClick = async (): Promise<void> => {
        try {
            await toggleWishlistItem(productId);
            await window.yieldToMainThread();
            const trackingType = isProductInWishlist
                ? WishListEventType.RemoveFromWishlist
                : WishListEventType.AddToWishlist;

            trackToggleWishlist({
                type: trackingType,
                productId,
                productTitle: product.title,
            });

            if (!isProductInWishlist) {
                trackEecToggleWishlistItem({
                    product: {
                        id: productId,
                        url,
                        trackingName,
                        manufacturerSku,
                        trackingPath,
                        price,
                        originalPrice,
                        title,
                        rating: product.rating,
                        ...(!isValueVoucher(product)
                            ? {
                                  participants: {
                                      min: product.minParticipants,
                                      max: product.maxParticipants,
                                  },
                              }
                            : {}),
                        ...(isExperience(product)
                            ? {
                                  locationCount: product.locations.length,
                              }
                            : {}),
                        nudge: product.productNudge
                            ? {
                                  id: product.productNudge?.id,
                                  text: product.productNudge?.text,
                              }
                            : undefined,
                        discountBadgeId: product.discountBadge?.id,
                        discountBadgeName: product.discountBadge?.text,
                        promotion:
                            product.promotionType &&
                            product.promotionType !== ProductPromotionType.NONE
                                ? ProductPromotionTypeTrackingLabel[product.promotionType]
                                : undefined,
                    },
                    quantity: 1,
                    variant: getPurchaseVariantForTracking(type, purchaseType),
                    eventName: isProductInWishlist
                        ? GA4EventName.RemoveFromWishlist
                        : GA4EventName.AddToWishlist,
                });
            }
        } catch (error) {
            if (
                (error as WishlistResponseError).response?.data?.message ===
                    WishlistErrorTypes.COUNT_EXCEEDED ||
                (error as Error).message === WishlistErrorTypes.COUNT_EXCEEDED
            ) {
                openSnackbar({
                    content: intl.formatMessage(messages.tooManyWishlistArticlesError),
                    variant: NotificationVariant.Error,
                    a11yTextCloseButton: intl.formatMessage(messages.a11YCloseErrorMessage),
                });

                trackWishlistError(
                    WishlistError.MoreThan99Products,
                    intl.formatMessage(messages.tooManyWishlistArticlesError),
                );
            } else {
                logToSentry(error as Error);
            }
        }
    };

    return (
        <div className={styles.productDetail} data-root="algolia-search">
            <div className={classNames(styles.withOverlay, isRecommendationsEnabled && 'd-none')} />
            <NotificationSnackbar
                className={upToXSBreakpoint ? 'container' : 'px-3x'}
                position={SnackbarPosition.Bottom}
                a11yCloseButtonText={intl.formatMessage(messages.a11YCloseErrorMessage)}
            />
            <div
                className={classNames(
                    'position-relative overflow-hidden',
                    styles.imageContainerWrapper,
                )}
            >
                {!isBoxProductWithQrCodeLink && discountBadge && (
                    <DiscountBadge
                        percentage={discountBadge?.percentage}
                        text={discountBadge.text}
                        shape={isMydays ? DiscountBadgeShape.Pill : DiscountBadgeShape.Rectangle}
                        withHint={isMydays}
                        primaryColor={discountBadge.primaryColor}
                        secondaryColor={discountBadge.secondaryColor}
                        textColor={discountBadge.textColor}
                        isBig
                        className={classNames('position-absolute', styles.discountBadgeWrapper)}
                    />
                )}

                <WishlistButton
                    onClick={toggleWishlistItemOnClick}
                    isActive={isProductInWishlist}
                    className={classNames(
                        'position-absolute flex-shrink-0 d-flex d-xs-none',
                        styles.wishlistButtonWrapper,
                    )}
                    a11yText={intl.formatMessage(
                        isProductInWishlist
                            ? messages.screenReaderTextForWishlistRemove
                            : messages.screenReaderTextForWishlistAdd,
                    )}
                    data-testid="wishlist-toggle-button-pdp"
                    isTransparent
                    wishlistButtonType={WishlistButtonType.Default}
                />

                <ImageGalleryWithFallback title={title} images={images} />
            </div>
            <article className={styles.buyboxWrapper}>
                <BuyboxContainer
                    handleWishlistButtonClick={toggleWishlistItemOnClick}
                    boxClusterAreaRef={boxClusterAreaRef}
                />
            </article>
            <div className={classNames(styles.infoWrapper, 'mt-sm-2x px-3x px-sm-0')}>
                <AttributesContainer attributes={attributes} />
                <ProductInfo
                    numTickets={Number(seatCount || 1)}
                    boxClusterAreaRef={boxClusterAreaRef}
                />
            </div>
        </div>
    );
};

export { ProductDetail };
